import { captureCallTiming } from './callTiming';
import api, { idemKey, captureCareerSession } from '@/lib/api';

const memory = new Map();
const actions = new Set(["not-reached", "no-interest", "callback-requested", "reached", "followup-not-reached"]);
const storageKey = userId => `ls_call_outcome_v2:${userId}`;
function read(userId) {
  if (memory.has(userId)) return memory.get(userId);
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey(userId)) || 'null');
    const job = stored && actions.has(stored.action) && typeof (stored.body?.lead_id || stored.body?.followup_id) === 'string'
      && typeof stored.body?.idempotency_key === 'string' ? stored : null;
    memory.set(userId, job); return job;
  }
  catch (_) { return null; }
}
function save(userId, job) {
  memory.set(userId, job);
  try { if (job) sessionStorage.setItem(storageKey(userId), JSON.stringify(job)); else sessionStorage.removeItem(storageKey(userId)); } catch (_) {}
}
export const pendingCallOutcome = userId => read(userId);
export function acknowledgeCallOutcome(userId, leadId) {
  if (read(userId)?.body.lead_id === leadId) save(userId, null);
}
const uncertain = error => !error?.response || error.response.status >= 500 || error.response.status === 408;

async function execute(userId, job, persistence, canRun = () => true) {
  const persist = persistence || (value => save(userId, value));
  const guard = () => {
    if (!canRun()) { const error = new Error('Anmeldung gewechselt. Vorgang bleibt zur Prüfung gespeichert.'); error.callOutcomePending = true; throw error; }
  };
  let lastError;
  for (let attempt = 0; attempt < 2; attempt++) {
    guard();
    // Recovery always asks for the committed receipt before another identical submit.
    if (job.attempted) {
      try {
        const {data} = await api.get('/call/action-status', {params: {
          action: job.action, lead_id: (job.body.lead_id || job.body.followup_id), idempotency_key: job.body.idempotency_key,
        }});
        if (data.status === 'done') return {data:data.response};
      } catch (error) { lastError = error; }
    }
    // Never automatically replay after the server receipt retention window.
    if (job.createdAt && Date.now() - job.createdAt > 23 * 60 * 60 * 1000) break;
    guard();
    job.attempted = true; persist(job);
    try {
      return await api.post(`/call/${job.action}`, job.body, {timeout:30000, careerSessionId:job.session, callTiming:job.callTiming || null});
    } catch (error) {
      lastError = error;
      if (!uncertain(error) && error.response.status !== 409) {
        // An earlier lost response can still have committed. Never infer failure from a 404.
        try {
          const {data} = await api.get('/call/action-status', {params:{action:job.action,
            lead_id:(job.body.lead_id || job.body.followup_id),idempotency_key:job.body.idempotency_key}});
          if (data.status === 'done') return {data:data.response};
          if (data.status === 'unknown') { persist(null); if (error.response.status === 404) error.callOutcomeRejected = true; throw error; }
        } catch (statusError) { if (statusError === error) throw error; }
      }
    }
  }
  const error = new Error('Bestätigung noch offen. Der Vorgang bleibt gespeichert und wird erneut geprüft.');
  error.callOutcomePending = true; error.cause = lastError;
  throw error;
}

export async function submitCallOutcome(userId, action, body) {
  let job = read(userId);
  if (job && (job.body.lead_id !== body.lead_id || job.action !== action)) {
    const error = new Error('Das vorherige Ergebnis wird noch bestätigt.'); error.callOutcomePending = true; throw error;
  }
  if (!job) {
    job = {action, callTiming:captureCallTiming(), createdAt:Date.now(), body:{...body, idempotency_key:body.idempotency_key || idemKey()},
      session:await captureCareerSession?.(), attempted:false};
    save(userId, job);
  }
  return execute(userId, job);
}
export async function recoverCallOutcome(userId) {
  const job = read(userId);
  if (!job) return null;
  const result = await execute(userId, job);
  acknowledgeCallOutcome(userId, job.body.lead_id);
  return {...result, leadId:job.body.lead_id};
}
export function resetCallOutcomesForTests() { memory.clear(); }

export const executeCallOutcome = (job, persist, canRun) => execute(null, job, persist, canRun);
